import { useState } from "react";
import { CalendarPlus, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { LeadFormData } from "../types";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";

interface Props {
  data: LeadFormData;
  onChange: (patch: Partial<LeadFormData>) => void;
  onSavePartnerInvite?: (email: string) => Promise<void> | void;
}

const CHECKLIST = [
  "Add the calendar invite so you don't miss the call.",
  "Have a quick think about what's missing from your current site.",
  "Show up on time - we keep these calls tight and high-value.",
];

const EVENT_TITLE = "Free Mockup Strategy Call — Mind Media";
const EVENT_DESCRIPTION =
  "We'll walk you through your custom mockup and a clear plan to launch.";

function formatGoogleDate(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function buildGoogleUrl(start?: string, end?: string) {
  if (!start || !end) return "#";
  const dates = `${formatGoogleDate(start)}/${formatGoogleDate(end)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: EVENT_TITLE,
    dates,
    details: EVENT_DESCRIPTION,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildOutlookUrl(start?: string, end?: string) {
  if (!start || !end) return "#";
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: EVENT_TITLE,
    body: EVENT_DESCRIPTION,
    startdt: start,
    enddt: end,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function buildIcs(start?: string, end?: string): string {
  if (!start || !end) return "";
  const dtStart = formatGoogleDate(start);
  const dtEnd = formatGoogleDate(end);
  const now = formatGoogleDate(new Date().toISOString());
  const uid = `mindmedia-${Date.now()}@mind-media`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Mind Media//Lead Form//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${EVENT_TITLE}`,
    `DESCRIPTION:${EVENT_DESCRIPTION}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

function downloadIcs(start?: string, end?: string) {
  const ics = buildIcs(start, end);
  if (!ics) return;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mind-media-call.ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function StepBookedSide({ data, onChange, onSavePartnerInvite }: Props) {
  const [savingInvite, setSavingInvite] = useState(false);
  const [inviteSaved, setInviteSaved] = useState(false);




  const start = data.booking?.startTime;
  const end = data.booking?.endTime;
  const calBtn =
    "inline-flex items-center gap-2.5 rounded-full border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground transition-colors hover:border-brand-blue hover:text-brand-blue";
  const calIcon = "h-5 w-5 shrink-0 object-contain";

  const handleSaveInvite = async () => {
    const email = (data.partnerInviteEmail ?? "").trim();
    if (!email || !onSavePartnerInvite) return;
    setSavingInvite(true);
    setInviteSaved(false);
    try {
      await onSavePartnerInvite(email);
      setInviteSaved(true);
    } finally {
      setSavingInvite(false);
    }
  };

  return (
    <div className="relative z-10 space-y-6">
      {/* Top row: Add to calendar + video */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
        <div className="rounded-xl border border-border bg-background/50 p-5">
          <div className="flex items-center gap-2 text-base font-semibold text-foreground">
            <CalendarPlus className="h-5 w-5 text-brand-blue" />
            Add to your calendar
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <a className={calBtn} href={buildGoogleUrl(start, end)} target="_blank" rel="noopener noreferrer">
              <img src="/icons/google-calendar.svg" alt="" className={calIcon} />
              Google Calendar
            </a>
            <button type="button" className={calBtn} onClick={() => downloadIcs(start, end)}>
              <img src="/icons/apple-calendar.svg" alt="" className={calIcon} />
              Apple Calendar
            </button>
            <a className={calBtn} href={buildOutlookUrl(start, end)} target="_blank" rel="noopener noreferrer">
              <img src="/icons/outlook.svg" alt="" className={calIcon} />
              Outlook
            </a>
          </div>
        </div>

        <ShareloEmbed id="Pl7YMKbkCjXLVT5cKzMNN" title="Booked — what happens next" className="hidden lg:block" />

      </div>


      <div className="rounded-xl border border-border bg-background/50 p-5">
        <div className="text-base font-semibold text-foreground">Do this before our call</div>
        <ul className="mt-3 space-y-2">
          {CHECKLIST.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-blue" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border border-border bg-background/50 p-5">
        <div className="text-base font-semibold text-foreground">
          Want a business partner or decision-maker on the call?
        </div>
        <div className="mt-3 space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="partnerInvite"
              type="email"
              placeholder="partner@business.com"
              maxLength={255}
              value={data.partnerInviteEmail ?? ""}
              onChange={(e) => {
                onChange({ partnerInviteEmail: e.target.value });
                setInviteSaved(false);
              }}
              onBlur={handleSaveInvite}
              className="h-11 rounded-xl"
            />
            <button
              type="button"
              onClick={handleSaveInvite}
              disabled={savingInvite || !(data.partnerInviteEmail ?? "").trim()}
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-brand-blue px-4 text-xs font-semibold text-brand-blue-foreground transition-colors hover:bg-brand-blue-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {inviteSaved ? (
                <>
                  <Check className="h-4 w-4" /> Sent
                </>
              ) : savingInvite ? (
                "Saving…"
              ) : (
                "Send invite"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
